"use client";

import { FeatherFlame } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import type { RegisteredUnitFormValues } from "@/components/registered-units/RegisteredUnitFormDialog";
import { RegisteredUnitFormDialog } from "@/components/registered-units/RegisteredUnitFormDialog";
import type {
  RegisteredUnit,
  RegisteredUnitCopy,
} from "@/components/registered-units/RegisteredUnitTable";
import { RegisteredUnitTable } from "@/components/registered-units/RegisteredUnitTable";
import {
  useCreateFireExtinguisherCommandControllerCreate,
  useListFireExtinguishersQueryControllerList,
  useUpdateFireExtinguisherCommandControllerUpdate,
} from "@/generated/fire-extinguishers/fire-extinguishers";

const COPY: RegisteredUnitCopy = {
  title: "Fire Extinguishers",
  description:
    "Register the extinguishers checked during the monthly inspection",
  addLabel: "Add Extinguisher",
  emptyTitle: "No fire extinguishers yet",
  emptyBody:
    "Register each extinguisher and its location so staff can check them during the monthly inspection.",
  emptyIcon: <FeatherFlame />,
  itemNoun: "extinguishers",
  errorMessage: "Error loading fire extinguishers. Please try again.",
};

const FORM_COPY = {
  unitLabel: "Fire Extinguisher",
  namePlaceholder: "e.g., Extinguisher 1 — Main Kitchen",
  notesPlaceholder: "e.g., CO2 type, serviced 2026-01-10",
  noRoomsHelpText:
    "Add at least one active room before registering an extinguisher.",
};

export function FireExtinguisherTable({ nurseryId }: { nurseryId: string }) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<RegisteredUnit | null>(null);

  const {
    data: unitsData,
    isLoading,
    error,
  } = useListFireExtinguishersQueryControllerList(nurseryId, {
    includeInactive: true,
  });

  const units = useMemo(() => unitsData?.data ?? [], [unitsData]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        typeof query.queryKey[0] === "string" &&
        query.queryKey[0].includes("/fire-extinguishers"),
      refetchType: "all",
    });
  };

  const { mutate: createUnit, isPending: isCreating } =
    useCreateFireExtinguisherCommandControllerCreate({
      mutation: {
        onSuccess: async () => {
          toast.success("Extinguisher added successfully");
          await invalidate();
          setDialogOpen(false);
          setEditingUnit(null);
        },
        onError: (e: unknown) =>
          toast.error(
            e instanceof Error ? e.message : "Failed to add extinguisher",
          ),
      },
    });

  const { mutate: updateUnit, isPending: isUpdating } =
    useUpdateFireExtinguisherCommandControllerUpdate({
      mutation: {
        onSuccess: async () => {
          await invalidate();
        },
        onError: (e: unknown) =>
          toast.error(
            e instanceof Error ? e.message : "Failed to update extinguisher",
          ),
      },
    });

  const handleToggleActive = (unit: RegisteredUnit) =>
    updateUnit(
      {
        nurseryId,
        fireExtinguisherId: unit.id,
        data: { isActive: !unit.isActive },
      },
      { onSuccess: () => toast.success("Extinguisher status updated") },
    );

  const handleSubmit = (values: RegisteredUnitFormValues) => {
    // Unreachable (the form requires a room here); narrows the shared optional
    // type without a cast.
    const roomId = values.roomId;
    if (!roomId) return;

    if (editingUnit) {
      updateUnit(
        {
          nurseryId,
          fireExtinguisherId: editingUnit.id,
          data: { ...values, roomId, notes: values.notes ?? null },
        },
        {
          onSuccess: () => {
            toast.success("Extinguisher updated successfully");
            setDialogOpen(false);
            setEditingUnit(null);
          },
        },
      );
      return;
    }
    createUnit({ nurseryId, data: { ...values, roomId } });
  };

  return (
    <>
      <RegisteredUnitTable
        units={units}
        isLoading={isLoading}
        error={error}
        copy={COPY}
        onAdd={() => {
          setEditingUnit(null);
          setDialogOpen(true);
        }}
        onEdit={(unit) => {
          setEditingUnit(unit);
          setDialogOpen(true);
        }}
        onToggleActive={handleToggleActive}
      />
      <RegisteredUnitFormDialog
        nurseryId={nurseryId}
        unit={editingUnit}
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingUnit(null);
        }}
        onSubmit={handleSubmit}
        isPending={isCreating || isUpdating}
        copy={FORM_COPY}
      />
    </>
  );
}
